import React from 'react'
import { observer } from 'mobx-react-lite'
import { v4 as uuidv4 } from 'uuid'
import { Form, message, Radio, Row, Space } from 'antd-v4'
import Text from 'antd-v4/lib/typography/Text'
import { ILocationZoneContract, LOCATION_ZONE_OPTION } from '@getgreenline/blaze-ecomm'
import { ConfigureEcomDelivery } from './ConfigureEcomDelivery'
import { ConfigureEcomDeliveryComponent } from './ConfigureEcomDeliveryComponent'
import { parseErrorMsg } from '../../../../utilities/helpers'

interface Props {
  configureEcomDelivery: ConfigureEcomDelivery
  flatRateFee: number
  updateFlatRateFee: (flatRateFee: number) => void
  visible: boolean
}

export const ConfigureEcomDeliveryRadioForm = observer(
  ({ configureEcomDelivery, flatRateFee, updateFlatRateFee, visible }: Props) => {
    const [legacyLocationZoneOption, setLegacyLocationZoneOption] =
      React.useState<LOCATION_ZONE_OPTION>(configureEcomDelivery.locationZoneOption)

    const isPostalCodeDisabled = React.useMemo(
      () => legacyLocationZoneOption !== LOCATION_ZONE_OPTION.POSTAL_CODE,
      [legacyLocationZoneOption],
    )

    const changeLocationZoneOption = (value: LOCATION_ZONE_OPTION) => {
      const updateLocationZones = [...configureEcomDelivery.locationZonesToShow]

      const flatRateObject: ILocationZoneContract = {
        locationZoneOption: LOCATION_ZONE_OPTION.FLAT_RATE,
        name: configureEcomDelivery.FLAT_RATE_NAME,
        fee: configureEcomDelivery.flatRateObject?.fee || 0,
        indexOrder: 1,
        postalCodes: ['*'],
        groupId: configureEcomDelivery.flatRateObject?.groupId || uuidv4(),
        companyId: configureEcomDelivery.config.companyId,
        locationId: configureEcomDelivery.config.id,
      }

      try {
        configureEcomDelivery.validateFieldCompletion()

        switch (value) {
          case LOCATION_ZONE_OPTION.FLAT_RATE:
            updateLocationZones.unshift(flatRateObject)
            break
          case LOCATION_ZONE_OPTION.POSTAL_CODE:
          case LOCATION_ZONE_OPTION.KML:
            // Default postal code for inactive flat fee state ('Z9Z9Z9')
            // Chosen as an uncommon and distinguishable code to represent the inactive flat fee state
            flatRateObject.postalCodes = ['Z9Z9Z9']
            flatRateObject.indexOrder = updateLocationZones.length + 1
            updateLocationZones.push(flatRateObject)
            break
        }

        configureEcomDelivery.setActiveKeys([])
        configureEcomDelivery.setAllLocationZones(updateLocationZones)

        configureEcomDelivery.setLocationZoneOption(value)
        setLegacyLocationZoneOption(value)
      } catch (error) {
        message.error(parseErrorMsg(error) || 'There was an error while updating Location zones')
      }
    }

    const overrideDummyPostalCode = () => {
      const {
        allLocationZones,
        flatRateObject,
        locationZoneOption,
        setLocationZoneOptionBeforeUpdate,
        setLocationZoneOption,
        FLAT_RATE_NAME,
        FLAT_RATE_DUMMY_POSTAL_CODE,
      } = configureEcomDelivery

      let overrideLocationZoneOption = locationZoneOption

      const dummyObject =
        flatRateObject || allLocationZones.find((lz) => lz.name === FLAT_RATE_NAME)

      if (
        dummyObject &&
        (dummyObject.postalCodes || []).length === 1 &&
        (dummyObject.postalCodes || [])[0] === FLAT_RATE_DUMMY_POSTAL_CODE
      ) {
        overrideLocationZoneOption = LOCATION_ZONE_OPTION.POSTAL_CODE
      }

      setLegacyLocationZoneOption(overrideLocationZoneOption)
      setLocationZoneOption(overrideLocationZoneOption)
      setLocationZoneOptionBeforeUpdate(overrideLocationZoneOption)
    }

    React.useEffect(() => {
      overrideDummyPostalCode()

      if (!configureEcomDelivery.isUsingLegacyLocationZones) {
        updateFlatRateFee(configureEcomDelivery.flatRateObject?.fee || 0)
      }
    }, [visible, configureEcomDelivery.isSaving])

    return (
      <Radio.Group
        style={{ width: '100%' }}
        value={legacyLocationZoneOption}
        onChange={({ target }) => changeLocationZoneOption(target.value)}
      >
        <Space direction='vertical'>
          <Radio value={LOCATION_ZONE_OPTION.FLAT_RATE}>
            <Text strong>{configureEcomDelivery.FLAT_RATE_NAME}</Text>
          </Radio>
          <Form style={{ padding: '0 24px' }}>
            <ConfigureEcomDeliveryComponent.FlatRate
              flatRateFee={flatRateFee}
              updateFlatRateFee={updateFlatRateFee}
              disabled={!isPostalCodeDisabled}
            />
          </Form>

          <Radio value={LOCATION_ZONE_OPTION.POSTAL_CODE}>
            <Text strong>Zone</Text>
          </Radio>
          <Space direction='vertical' style={{ padding: '0 24px' }}>
            <Row style={{ display: 'flex', justifyContent: 'end' }}>
              <ConfigureEcomDeliveryComponent.LocationZoneToolbar
                addLocationZone={(override) =>
                  configureEcomDelivery.addLocationZone({
                    ...override,
                    locationZoneOption: LOCATION_ZONE_OPTION.POSTAL_CODE,
                  })
                }
                activeKeys={configureEcomDelivery.activeKeys}
                toggleActiveKeys={configureEcomDelivery.toggleActiveKeys}
                disabled={isPostalCodeDisabled}
              />
            </Row>
            <Row>
              {configureEcomDelivery.locationZonesToShow?.map((locationZone, index) => {
                return (
                  <ConfigureEcomDeliveryComponent.PostalCode
                    key={locationZone.groupId}
                    index={index}
                    locationZone={locationZone}
                    configureEcomDelivery={configureEcomDelivery}
                    disabled={isPostalCodeDisabled}
                  />
                )
              })}
            </Row>
          </Space>
        </Space>
      </Radio.Group>
    )
  },
)
